import styled from 'styled-components'
import { format } from 'date-fns'
import { Calendar } from 'lucide-react'
import { PiClockCountdown, PiPencilSimpleLine, PiRepeat } from 'react-icons/pi'
import { useGetReminderDetail } from 'services/reminders'
import { ReminderListItem } from 'models/reminders'
import { TextPill } from 'components/FbUI/TextPill'
import { reminderTypeFormat } from './utils'

interface MobileReminderCardProps {
  reminder: ReminderListItem
  onEdit?: (id: number) => void
  expanded?: boolean
  onToggleExpand?: () => void
}

const CardContainer = styled.div`
  background: white;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgb(0 0 0 / 0.1);
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const PillsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const Title = styled.h3<{ $expanded: boolean }>`
  font-size: 15px;
  font-weight: 600;
  color: #191d23;
  margin: 0 0 4px 0;
  line-height: 1.3;

  ${({ $expanded }) =>
    !$expanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`

const Content = styled.p<{ $expanded: boolean }>`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 12px 0;
  line-height: 1.4;

  ${({ $expanded }) =>
    !$expanded &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`

const DateRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: #6b7280;
  padding: 10px;
  margin-top: 4px;
  background: #f9fafb;
  border-radius: 0 0 8px 8px;
`

const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const statusColors: Record<string, { bg: string; text: string }> = {
  scheduled: { bg: '#F3E8FF', text: '#7C3AED' },
  sent: { bg: '#D1FAE5', text: '#047857' },
  failed: { bg: '#FEE2E2', text: '#DC2626' },
  deleted: { bg: '#E5E7EB', text: '#374151' },
  active: { bg: '#ECFCCB', text: '#4D7C0F' },
  paused: { bg: '#FEF3C7', text: '#B45309' },
  ended: { bg: '#FFEDD5', text: '#C2410C' },
}

const typeColors: Record<string, { bg: string; text: string }> = {
  one_off: { bg: '#D1FAE5', text: '#047857' },
  daily: { bg: '#DBEAFE', text: '#1D4ED8' },
  weekly: { bg: '#E0E7FF', text: '#4338CA' },
  monthly: { bg: '#FCE7F3', text: '#BE185D' },
  quarterly: { bg: '#FEF3C7', text: '#B45309' },
  annually: { bg: '#FFEDD5', text: '#C2410C' },
  custom: { bg: '#F3E8FF', text: '#7C3AED' },
}

export function MobileReminderCard({
  reminder,
  onEdit,
  expanded = false,
  onToggleExpand,
}: MobileReminderCardProps) {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    try {
      return format(new Date(dateString), 'MM/dd/yyyy')
    } catch {
      return dateString
    }
  }

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    try {
      return format(new Date(dateString), 'MM/dd/yyyy h:mm a')
    } catch {
      return dateString
    }
  }

  const statusColor = statusColors[reminder.status] || {
    bg: '#E5E7EB',
    text: '#374151',
  }
  const typeColor = typeColors[reminder.reminder_type] || {
    bg: '#E5E7EB',
    text: '#374151',
  }

  const isRecurring = reminder.reminder_type !== 'one_off'
  const TypeIcon = isRecurring ? PiRepeat : PiClockCountdown

  const statusDisplay =
    reminder.status.charAt(0).toUpperCase() + reminder.status.slice(1)

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onEdit?.(reminder.id)
  }

  // Detail query: gives recipients when expanded
  const { data: detail } = useGetReminderDetail(
    { id: reminder.id },
    { query: { enabled: expanded } }
  )

  const recipients = detail?.recipients
  const companyName = reminder.company_name || detail?.company_name || ''
  const companyLink = reminder.company_notification_link

  return (
    <CardContainer onClick={onToggleExpand}>
      <div className="p-3">
        <CardHeader>
          <PillsRow>
            <TextPill backgroundColor={typeColor.bg} textColor={typeColor.text}>
              <TypeIcon size={12} style={{ marginRight: 4 }} />
              {reminderTypeFormat(reminder)}
            </TextPill>
            <TextPill
              backgroundColor={statusColor.bg}
              textColor={statusColor.text}
            >
              {statusDisplay}
            </TextPill>
          </PillsRow>
          {onEdit && reminder.status !== 'deleted' && (
            <button
              onClick={handleEditClick}
              className="flex h-6 w-6 items-center justify-center rounded bg-surface-hover transition-colors hover:bg-gray-50"
              aria-label="Edit deal"
            >
              <PiPencilSimpleLine className="h-3.5 w-3.5 text-brand" />
            </button>
          )}
        </CardHeader>

        <Title $expanded={expanded}>{reminder.title}</Title>
        {reminder.content && (
          <Content $expanded={expanded}>{reminder.content}</Content>
        )}

        {expanded && (
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
            {(companyName || companyLink) && (
              <div style={{ marginBottom: 6 }}>
                <span style={{ fontWeight: 600 }}>Company </span>
                {companyLink ? (
                  <a
                    href={companyLink}
                    style={{ color: '#15803d', textDecoration: 'underline' }}
                  >
                    {companyName}
                  </a>
                ) : (
                  <span>{companyName || '-'}</span>
                )}
              </div>
            )}

            {recipients && recipients.length > 0 && (
              <div
                style={{
                  marginBottom: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  <span style={{ fontWeight: 600 }}>Recipients </span>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: 999,
                      backgroundColor: '#E5E7EB',
                      fontSize: 12,
                      marginLeft: 6,
                    }}
                  >
                    {recipients[0].first_name} {recipients[0].last_name}
                  </span>
                  {recipients.length > 1 && (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 999,
                        border: '1px solid #D1D5DB',
                        fontSize: 12,
                        marginLeft: 4,
                      }}
                    >
                      +{recipients.length - 1}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
              <span style={{ fontWeight: 600 }}>Created </span>
              {formatDateTime(reminder.created)}
            </div>
          </div>
        )}
      </div>
      <DateRow>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            flex: 1,
          }}
        >
          <DateInfo>
            <Calendar size={12} />
            <span>Scheduled {formatDateTime(reminder.run_at)}</span>
          </DateInfo>
        </div>
        {reminder.end_date && (
          <span>End Date {formatDate(reminder.end_date)}</span>
        )}
      </DateRow>
    </CardContainer>
  )
}
